import React, { useEffect, useMemo, useState } from 'react';
import api, { extractApiError, isRequestCanceled, unwrapApiResponse } from '../api/api';
import { useToast } from './ToastProvider';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'admin' },
  { value: 'reports_reader', label: 'reports_reader' },
  { value: 'ingestion_operator', label: 'ingestion_operator' }
];

function normalizeTenantList(values = []) {
  return Array.from(new Set((values || []).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)));
}

function TenantMultiSelect({ options, selectedValues, onToggle, placeholder = 'Selecciona Entidades' }) {
  const normalizedValues = normalizeTenantList(selectedValues);
  const selectedLabels = options.filter((option) => normalizedValues.includes(option.value)).map((option) => option.label);

  return (
    <details className="field entity-multiselect">
      <summary className="entity-multiselect-trigger">
        <div>
          <span className="entity-multiselect-label">Entidades</span>
          <strong>{selectedLabels.length ? `${selectedLabels.length} seleccionada(s)` : placeholder}</strong>
        </div>
        <span className="entity-multiselect-hint">{options.length} disponible(s)</span>
      </summary>
      <div className="entity-multiselect-panel">
        {options.length ? options.map((option) => {
          const checked = normalizedValues.includes(option.value);
          return (
            <label key={option.value} className="entity-option-row">
              <input type="checkbox" checked={checked} onChange={() => onToggle(option.value)} />
              <span>{option.label}</span>
            </label>
          );
        }) : <span className="section-subtitle">Sin entidades disponibles</span>}
      </div>
    </details>
  );
}

export default function UserAdminView({ getToken, tenantOptions = [] }) {
  const { showError, showSuccess } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'reports_reader',
    tenants: []
  });

  const availableTenants = useMemo(
    () => tenantOptions.map((item) => ({ label: item.label, value: String(item.value || '').toLowerCase() })),
    [tenantOptions]
  );

  useEffect(() => {
    let controller;
    let alive = true;

    async function loadUsers() {
      controller?.abort?.();
      controller = new AbortController();
      try {
        setLoading(true);
        const token = await getToken({ skipCache: true });
        const response = await api.get('/admin/users', {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        if (!alive) return;
        const payload = unwrapApiResponse(response);
        setUsers((payload?.users || []).map((user) => ({
          ...user,
          tenants: normalizeTenantList(user.tenants)
        })));
        setLoading(false);
      } catch (error) {
        if (!alive || isRequestCanceled(error)) return;
        setLoading(false);
        showError(extractApiError(error, 'No se pudo cargar la lista de usuarios'));
      }
    }

    loadUsers();
    return () => {
      alive = false;
      controller?.abort?.();
    };
  }, [getToken, showError]);

  function updateUserDraft(userId, patch) {
    setUsers((current) => current.map((user) => user.id === userId ? { ...user, ...patch } : user));
  }

  function updateNewUserDraft(patch) {
    setNewUser((current) => ({ ...current, ...patch }));
  }

  function handleRoleChange(userId, role) {
    setUsers((current) => current.map((user) => {
      if (user.id !== userId) return user;
      return {
        ...user,
        role,
        tenants: role === 'reports_reader'
          ? normalizeTenantList(user.tenants)
          : []
      };
    }));
  }

  function toggleTenant(userId, tenantValue) {
    setUsers((current) => current.map((user) => {
      if (user.id !== userId) return user;
      const currentTenants = normalizeTenantList(user.tenants);
      const nextTenants = currentTenants.includes(tenantValue)
        ? currentTenants.filter((value) => value !== tenantValue)
        : [...currentTenants, tenantValue];
      return { ...user, tenants: nextTenants };
    }));
  }

  function toggleNewUserTenant(tenantValue) {
    const currentTenants = normalizeTenantList(newUser.tenants);
    const nextTenants = currentTenants.includes(tenantValue)
      ? currentTenants.filter((value) => value !== tenantValue)
      : [...currentTenants, tenantValue];
    updateNewUserDraft({ tenants: nextTenants });
  }

  async function createUser() {
    try {
      setCreating(true);
      const token = await getToken({ skipCache: true });
      const response = await api.post('/admin/users', {
        email: newUser.email,
        password: newUser.password,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        tenants: newUser.role === 'reports_reader' ? normalizeTenantList(newUser.tenants) : []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiResponse(response);
      setUsers((current) => [{
        ...payload,
        tenants: normalizeTenantList(payload.tenants)
      }, ...current]);
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'reports_reader',
        tenants: []
      });
      showSuccess(`Usuario creado: ${payload.email || payload.fullName || payload.id}`, { duration: 2500 });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      showError(extractApiError(error, 'No se pudo crear el usuario'));
    } finally {
      setCreating(false);
    }
  }

  async function saveUser(user) {
    try {
      setSavingUserId(user.id);
      const token = await getToken({ skipCache: true });
      const response = await api.patch(`/admin/users/${user.id}`, {
        role: user.role,
        tenants: user.role === 'reports_reader' ? normalizeTenantList(user.tenants) : []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiResponse(response);
      updateUserDraft(user.id, {
        ...payload,
        tenants: normalizeTenantList(payload.tenants)
      });
      showSuccess(`Usuario actualizado: ${payload.email || payload.fullName || payload.id}`, { duration: 2500 });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      showError(extractApiError(error, 'No se pudo actualizar el usuario'));
    } finally {
      setSavingUserId(null);
    }
  }

  return (
    <section className="monitor-layout">
      <section className="table-card">
        <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
          <div>
            <div className="brand-badge">Admin</div>
            <h2 className="section-title with-margin">Usuarios y entidades</h2>
            <p className="section-subtitle">Asigna roles y entidades visibles para usuarios activos. Las entidades sólo aplican para el rol `reports_reader`.</p>
          </div>
        </div>

        <form
          className="user-admin-creator"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            createUser();
          }}
        >
          <div className="user-admin-form-grid">
            <label className="field">
              <span>Nombre</span>
              <input type="text" autoComplete="off" value={newUser.firstName} onChange={(event) => updateNewUserDraft({ firstName: event.target.value })} />
            </label>
            <label className="field">
              <span>Apellido</span>
              <input type="text" autoComplete="off" value={newUser.lastName} onChange={(event) => updateNewUserDraft({ lastName: event.target.value })} />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                name="new-user-email"
                autoComplete="off"
                value={newUser.email}
                onChange={(event) => updateNewUserDraft({ email: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Password</span>
              <input
                type="password"
                name="new-user-password"
                autoComplete="new-password"
                value={newUser.password}
                onChange={(event) => updateNewUserDraft({ password: event.target.value })}
              />
            </label>
            <label className="field user-admin-role-field">
              <span>Rol</span>
              <select
                value={newUser.role}
                onChange={(event) => updateNewUserDraft({
                  role: event.target.value,
                  tenants: event.target.value === 'reports_reader' ? newUser.tenants : []
                })}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {newUser.role === 'reports_reader' ? (
              <div className="user-admin-entity-field">
                <TenantMultiSelect
                  options={availableTenants}
                  selectedValues={newUser.tenants}
                  onToggle={toggleNewUserTenant}
                  placeholder="Selecciona Entidades"
                />
              </div>
            ) : (
              <div className="field user-admin-entity-placeholder">
                <span>Entidades</span>
                <div className="ingestion-summary-box">No aplica para este rol</div>
              </div>
            )}
          </div>

          <div className="user-admin-create-actions">
            <button type="submit" className="primary-button" disabled={creating || !newUser.email || !newUser.password || !newUser.role}>
              {creating ? 'Creando...' : 'Crear usuario'}
            </button>
          </div>
        </form>

        {loading ? <p className="section-subtitle">Cargando usuarios...</p> : null}

        <div className="table-shell report-table-shell">
          <table className="report-table responsive-job-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Entidades</th>
                <th>Última actividad</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {users.length ? users.map((user) => (
                <tr key={user.id}>
                  <td>{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.id}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <select value={user.role || ''} onChange={(event) => handleRoleChange(user.id, event.target.value)}>
                      <option value="" disabled>Selecciona rol</option>
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {user.role === 'reports_reader' ? (
                      <TenantMultiSelect
                        options={availableTenants}
                        selectedValues={user.tenants}
                        onToggle={(tenantValue) => toggleTenant(user.id, tenantValue)}
                        placeholder="Selecciona Entidades"
                      />
                    ) : (
                      <span className="section-subtitle">No aplica</span>
                    )}
                  </td>
                  <td>{user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleString() : '-'}</td>
                  <td>
                    <button
                      type="button"
                      className="primary-button"
                      onClick={() => saveUser(user)}
                      disabled={savingUserId === user.id || !user.role}
                    >
                      {savingUserId === user.id ? 'Guardando...' : 'Guardar'}
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="6" className="empty-cell">Sin usuarios activos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
